import { Arrange, Link } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COMMENTS_BREAKPOINT } from 'constants/breakpoints';
import { REPLY_ID_QUERY_PARAM } from 'constants/mention';
import { deleteReplyCreator } from 'creators/comments';
import { selectCurrentUserIsOwner } from 'selectors/videoPlayer';
import { getCloudfrontURI } from 'utilities/avatar';
import { getFormattedDateForHeader, userCanDelete } from 'utilities/comments';
import { getParam, removeParam } from 'utilities/url';

import useDestination from 'hooks/useDestination';
import useMedia from 'hooks/useMedia';
import { useScrollToCallbackRef } from 'hooks/useScrollToCallbackRef';

import DeleteUndoPlaceholder from '../delete-undo-placeholder';
import PostBody from '../post-body';
import { HighlightReplyContext } from './HighlightReplyContext';

import UserAvatar from 'components/user-avatar';
import ProfileCard from 'components/user-profile/profile-card';

import styles from './styles.module.less';

const AVATAR_SIZE_FULL = 4;
const AVATAR_SIZE_SMALL = 3;

const Reply = props => {
  const { reply, scrollContainer, highlightStyle } = props;

  const dispatch = useDispatch();
  const currentUserIsOwner = useSelector(selectCurrentUserIsOwner);
  const user = useSelector(state => state.user);

  const replyRef = useRef(null);

  const avatar = reply.avatar && reply.avatar.thumb;

  const deleteReply = () => {
    dispatch(deleteReplyCreator(reply));
  };

  const fullSize = useMedia(
    [`(min-width: ${COMMENTS_BREAKPOINT}px)`],
    [true],
    false
  );
  const avatarSize = fullSize ? AVATAR_SIZE_FULL : AVATAR_SIZE_SMALL;

  const replyIdToHighlightFromUrl = getParam(REPLY_ID_QUERY_PARAM);
  const replyIdToHighlightFromContext = useContext(HighlightReplyContext);
  const [replyIdToDeeplink] = useState(
    replyIdToHighlightFromUrl || replyIdToHighlightFromContext
  );
  const shouldScrollToAndHighlightReply =
    replyIdToDeeplink === String(reply.id);
  const cleanupScroll = useCallback(node => {
    node.addEventListener('animationend', () => {
      removeParam(window)(REPLY_ID_QUERY_PARAM);
    });
  }, []);
  const scrollToAndHighlightReplyRef = useScrollToCallbackRef(
    shouldScrollToAndHighlightReply,
    scrollContainer,
    cleanupScroll
  );
  const showDestination = useDestination();

  function VisibleReply() {
    return (
      <>
        {showDestination === true && (
          <ProfileCard
            avatarSrc={avatar && getCloudfrontURI(avatar)}
            hasFocus={false}
            name={reply.user_name}
            profileId={reply.user_id}
          >
            <UserAvatar
              className={styles.avatar}
              avatarSrc={avatar && getCloudfrontURI(avatar)}
              name={reply.user_name}
              avatarSize={avatarSize}
            />
          </ProfileCard>
        )}
        {showDestination === false && (
          <UserAvatar
            className={styles.avatar}
            avatarSrc={avatar && getCloudfrontURI(avatar)}
            name={reply.user_name}
            avatarSize={avatarSize}
          />
        )}
        <div ref={replyRef} className={styles.commentReply}>
          <div className={styles.replyHeader}>
            <span className={styles.userName}>
              {showDestination === true && (
                <div className={styles.profileWrapper}>
                  <ProfileCard
                    avatarSrc={avatar && getCloudfrontURI(avatar)}
                    hasFocus={false}
                    name={reply.user_name}
                    profileId={reply.user_id}
                    placement="top-center"
                  >
                    {reply.user_name}
                  </ProfileCard>
                </div>
              )}
              {showDestination === false && reply.user_name}
            </span>
          </div>
          <PostBody content={reply}></PostBody>
          <div className={styles.dimmedText}>
            <span>{getFormattedDateForHeader(reply.createdAt)}</span>
            {userCanDelete(reply, currentUserIsOwner, user) && (
              <Link
                variant="neutral"
                className={styles.deleteLink}
                onClick={deleteReply}
              >
                Delete
              </Link>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className={classNames({
        [styles.replyContainer]: true,
        [highlightStyle]: shouldScrollToAndHighlightReply,
      })}
      ref={scrollToAndHighlightReplyRef}
    >
      <Arrange alignItems="start" gap="medium" id={`reply-${reply.id}`}>
        {reply.locallyDeleted ? (
          <DeleteUndoPlaceholder commentOrReply={reply}></DeleteUndoPlaceholder>
        ) : (
          VisibleReply()
        )}
      </Arrange>
    </div>
  );
};

export default Reply;
